/**
 * 运行环境识别：系统、浏览器、手机厂商。
 *
 * 这里的判断全部基于 UA（User-Agent）。UA 是不可信、可以被伪造的输入，
 * 所以只用于「决定展示顺序和提示文案」，绝不用于任何权限或安全判断。
 * 判错的后果是「推荐了不对的商店」，页面依然可用——这是有意为之的降级设计。
 */

import { stores } from '../config.js';

/** 读取 UA，在 SSR / 测试环境里安全返回空串。 */
function ua() {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
}

/**
 * 浏览器与内嵌 WebView 的识别表。
 *
 * blocksScheme 的含义：这类内置浏览器会拦截自定义 scheme 跳转，
 * 甚至把 https 的 Universal Link 也拦掉（微信最典型），
 * 所以在这种环境里不要去「尝试唤起」，而是直接引导用户换浏览器。
 *
 * 顺序很重要：企业微信 UA 里也含 MicroMessenger，
 * QQ 浏览器（MQQBrowser）也含 QQ/ —— 必须先匹配更具体的那个。
 */
const BROWSER_RULES = [
  { id: 'wechat-work', name: '企业微信', test: /wxwork/i, blocksScheme: true },
  { id: 'wechat', name: '微信', test: /MicroMessenger/i, blocksScheme: true },
  { id: 'qqbrowser', name: 'QQ 浏览器', test: /MQQBrowser/i, blocksScheme: false },
  { id: 'qq', name: 'QQ', test: /\bQQ\/[\d.]+/i, blocksScheme: true },
  { id: 'weibo', name: '微博', test: /Weibo/i, blocksScheme: true },
  { id: 'douyin', name: '抖音', test: /aweme|ByteLocale/i, blocksScheme: true },
  { id: 'xhs', name: '小红书', test: /XiaoHongShu/i, blocksScheme: true },
  { id: 'xiaoyuan', name: '闲鱼', test: /Xianyu/i, blocksScheme: true },
  { id: 'alipay', name: '支付宝', test: /AlipayClient/i, blocksScheme: false },
  { id: 'baidu', name: '百度', test: /baiduboxapp/i, blocksScheme: true },
  { id: 'uc', name: 'UC 浏览器', test: /UCBrowser|UCWEB/i, blocksScheme: false },
  { id: 'huaweibrowser', name: '华为浏览器', test: /HuaweiBrowser/i, blocksScheme: false },
  { id: 'miuibrowser', name: '小米浏览器', test: /MiuiBrowser/i, blocksScheme: false },
  { id: 'chrome', name: 'Chrome', test: /CriOS|Chrome/i, blocksScheme: false },
  { id: 'safari', name: 'Safari', test: /Safari/i, blocksScheme: false },
];

/** 当前浏览器信息。匹配不到时返回一个通用的「其他浏览器」。 */
export function detectBrowser() {
  const agent = ua();
  const hit = BROWSER_RULES.find((rule) => rule.test.test(agent));
  return hit ?? { id: 'other', name: '浏览器', blocksScheme: false };
}

/** 是否在「会拦截 scheme 的内置浏览器」里。 */
export function isBlockedBrowser() {
  return detectBrowser().blocksScheme;
}

/** iOS / iPadOS。 */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const agent = ua();
  if (/iPhone|iPad|iPod/i.test(agent)) return true;
  // iPadOS 13+ 默认把 UA 伪装成 macOS，只能靠「触屏点数」区分
  return /Macintosh/i.test(agent) && navigator.maxTouchPoints > 1;
}

/** Android（含鸿蒙的 Android 兼容模式）。 */
export function isAndroid() {
  return /Android|HarmonyOS/i.test(ua());
}

/** 手机 / 平板。桌面端不尝试唤起 App。 */
export function isMobile() {
  if (typeof navigator === 'undefined') return false;
  if (isIOS() || isAndroid()) return true;
  return /Mobile|IEMobile|Windows Phone/i.test(ua());
}

/** 桌面端。 */
export function isDesktop() {
  return !isMobile();
}

/**
 * 是否是「已经装到桌面上的 PWA」，此时没有地址栏，
 * 引导「点右上角用浏览器打开」就不成立了。
 */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.navigator.standalone === true) return true;
  return window.matchMedia?.('(display-mode: standalone)').matches ?? false;
}

/**
 * 按 UA 猜手机厂商，返回 share.config.mjs 里对应的商店 id。
 * 猜不到返回 null，由调用方走「通用商店」逻辑。
 */
export function detectBrandStoreId() {
  const agent = ua();
  if (!agent) return null;

  // 荣耀要先于华为判断：荣耀机型 UA 里不会再出现 HUAWEI
  const match = stores.find((store) => {
    if (!store.ua || store.type !== 'android') return false;
    return store.ua.test(agent);
  });

  return match ? match.id : null;
}

/** 当前设备「最该优先展示」的商店。 */
export function getPreferredStore(androidStores) {
  const brandId = detectBrandStoreId();
  if (brandId) {
    const hit = androidStores.find((store) => store.id === brandId);
    if (hit) return hit;
  }
  // 没识别出厂商时，应用宝是国内最通用的选择（微信里也能打开）
  return androidStores.find((store) => store.id === 'tencent') ?? androidStores[0] ?? null;
}

/** 当前环境的人类可读描述，用在提示文案里。 */
export function describeEnvironment() {
  if (isIOS()) return 'iPhone / iPad';
  if (isAndroid()) return 'Android 手机';
  return '电脑';
}
